(() => {
  const CENTIMETERS_PER_METER = 100;
  const DEFAULT_BUTTON_STEP_CM = 10;

  const parseMeters = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
    const meters = Number(normalized);
    return Number.isFinite(meters) ? meters : null;
  };

  const metersToCentimeters = (value) => {
    const meters = typeof value === 'number' ? value : parseMeters(value);
    if (meters === null || !Number.isFinite(meters)) return null;
    const centimeters = Math.round(meters * CENTIMETERS_PER_METER);
    if (Math.abs(centimeters / CENTIMETERS_PER_METER - meters) > 0.00001) return null;
    return centimeters;
  };

  const centimetersToMeters = (value) => {
    const centimeters = Number(value);
    if (!Number.isFinite(centimeters)) return '';
    return (centimeters / CENTIMETERS_PER_METER)
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1')
      .replace('.', ',');
  };

  window.KickisFabricUnits = {
    centimetersPerMeter: CENTIMETERS_PER_METER,
    parseMeters,
    metersToCentimeters,
    centimetersToMeters,
  };

  if (!window.customElements || typeof HTMLElement === 'undefined') return;

  class FabricQuantityInput extends HTMLElement {
    connectedCallback() {
      this.displayInput = this.querySelector('[data-fabric-meter-input]');
      this.shopifyInput = this.querySelector('[data-fabric-shopify-input]');
      this.minusButton = this.querySelector("button[name='minus']");
      this.plusButton = this.querySelector("button[name='plus']");

      if (!this.displayInput || !this.shopifyInput) return;

      this.onDisplayInput = this.onDisplayInput.bind(this);
      this.onDisplayChange = this.onDisplayChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onShopifyQuantityChange = this.syncFromShopify.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);

      this.displayInput.addEventListener('input', this.onDisplayInput);
      this.displayInput.addEventListener('change', this.onDisplayChange);
      this.displayInput.addEventListener('keydown', this.onKeyDown);
      this.shopifyInput.addEventListener('fabric:shopify-quantity-change', this.onShopifyQuantityChange);
      this.minusButton?.addEventListener('click', (event) => this.adjust(event, -1));
      this.plusButton?.addEventListener('click', (event) => this.adjust(event, 1));

      const formId = this.shopifyInput.getAttribute('form');
      this.form = formId ? document.getElementById(formId) : this.closest('form');
      this.form?.addEventListener('submit', this.onFormSubmit, true);

      this.syncFromShopify();
    }

    disconnectedCallback() {
      this.displayInput?.removeEventListener('input', this.onDisplayInput);
      this.displayInput?.removeEventListener('change', this.onDisplayChange);
      this.displayInput?.removeEventListener('keydown', this.onKeyDown);
      this.shopifyInput?.removeEventListener('fabric:shopify-quantity-change', this.onShopifyQuantityChange);
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
    }

    get minCm() {
      const value = this.shopifyInput.dataset.min ?? this.shopifyInput.min;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 1;
    }

    get maxCm() {
      const value = this.shopifyInput.max || this.shopifyInput.dataset.max;
      if (value === '' || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    get incrementCm() {
      const parsed = Number(this.shopifyInput.step);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    get buttonStepCm() {
      const parsed = Number(this.dataset.buttonStepCm);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_BUTTON_STEP_CM;
    }

    validateCentimeters(centimeters) {
      let message = '';
      if (centimeters === null) {
        message = 'Ange längden i meter med högst två decimaler, till exempel 0,5.';
      } else if (centimeters < this.minCm) {
        message = `Minsta valbara längd är ${centimetersToMeters(this.minCm)} meter.`;
      } else if (this.maxCm !== null && centimeters > this.maxCm) {
        message = `Det finns högst ${centimetersToMeters(this.maxCm)} meter tillgängligt.`;
      } else if (centimeters % this.incrementCm !== 0) {
        message = `Längden måste anges i steg om ${centimetersToMeters(this.incrementCm)} meter.`;
      }
      this.displayInput.setCustomValidity(message);
      return message === '';
    }

    syncToShopify({ normalize = false, dispatch = false } = {}) {
      const centimeters = metersToCentimeters(this.displayInput.value);
      if (!this.validateCentimeters(centimeters)) return false;

      this.shopifyInput.value = String(centimeters);
      if (normalize) this.displayInput.value = centimetersToMeters(centimeters);
      this.updateButtons(centimeters);

      if (dispatch) {
        this.shopifyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    syncFromShopify() {
      if (!this.shopifyInput || !this.displayInput) return;
      const centimeters = Number(this.shopifyInput.value);
      if (!Number.isFinite(centimeters)) return;
      this.displayInput.value = centimetersToMeters(centimeters);
      this.displayInput.setCustomValidity('');
      this.updateButtons(centimeters);
    }

    updateButtons(centimeters) {
      const atMinimum = centimeters <= this.minCm;
      const atMaximum = this.maxCm !== null && centimeters >= this.maxCm;
      this.minusButton?.classList.toggle('disabled', atMinimum);
      this.minusButton?.toggleAttribute('disabled', atMinimum);
      this.plusButton?.classList.toggle('disabled', atMaximum);
      this.plusButton?.toggleAttribute('disabled', atMaximum);
    }

    adjust(event, direction) {
      event.preventDefault();
      const parsed = metersToCentimeters(this.displayInput.value);
      let centimeters = parsed === null ? Number(this.shopifyInput.value) : parsed;
      if (!Number.isFinite(centimeters)) centimeters = this.minCm;
      centimeters += direction * this.buttonStepCm;
      centimeters = Math.max(this.minCm, centimeters);
      if (this.maxCm !== null) centimeters = Math.min(this.maxCm, centimeters);
      this.displayInput.value = centimetersToMeters(centimeters);
      this.syncToShopify({ normalize: true, dispatch: true });
    }

    onDisplayInput(event) {
      event.stopPropagation();
      this.syncToShopify();
    }

    onDisplayChange(event) {
      event.stopPropagation();
      if (!this.syncToShopify({ normalize: true, dispatch: true })) {
        this.displayInput.reportValidity();
      }
    }

    onKeyDown(event) {
      if (event.key === 'ArrowUp') this.adjust(event, 1);
      if (event.key === 'ArrowDown') this.adjust(event, -1);
    }

    onFormSubmit(event) {
      if (this.syncToShopify({ normalize: true })) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.displayInput.reportValidity();
      this.displayInput.focus();
    }

    resetFromShopify() {
      this.syncFromShopify();
    }
  }

  if (!customElements.get('fabric-quantity-input')) {
    customElements.define('fabric-quantity-input', FabricQuantityInput);
  }
})();
